import React, {
  ReactNode,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  InputAdornment,
  TextField,
  Button,
  Checkbox,
  IconButton,
  Switch,
  ThemeProvider,
  createTheme,
  Divider,
  Menu,
  MenuItem,
  ListItemText,
  Chip,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardDoubleArrowUpRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowUpRounded";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CodeIcon from "@mui/icons-material/Code";
import ShortlistedLeadsSidebar from "./Components/ShortListedSiderbar";
import OuterDiv from "components/Common/OuterDivComponent";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import GroupIcon from "@mui/icons-material/Group";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ShortlistedLeadsTable from "./Tabs/ShortListed-leads";
import { campaignManagementService } from "services/campaign";
import { RootState } from "store";
import { useSelector } from "react-redux";
import ShortlistedLeadsContainer from "./Components/shortlistedLeadsContainer";
import CampaignDetailsSkeleton from "./PreLoadSkeletons/CampaignDetailsSkeleton";
import CampaignTableSkeleton from "./PreLoadSkeletons/CampaignTableSkeleton";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { DEFAULT_TOKEN } from "utilities/constants";
import LeadDetailsDrawer from "./Components/LeadDetailsDrawer";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  PrioritizeDialog,
  FeedbackDialog,
  ShortlistConfigDialog,
} from "./Components/Dialogs";
import LeadsTable from "./Components/LeadsTable";
import CampaignHeader from "./Components/CampaignHeader";
import StatusCards from "./Components/StatusCards";
import { commonButtonStyle, tabStyle } from "./styles/commonStyles";
import { Batch } from "../../types";
import AddLeadDialog from "./Components/Dialogs/AddLeadDialog";
import NoAccountsIcon from "@mui/icons-material/NoAccounts";
import styled from "styled-components";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

const SidebarWrapper = styled(Box)(({ theme: Theme }) => ({
  width: "380px",
  marginTop: "10px",
  flexShrink: 0,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflow: "hidden",
  marginLeft: 0,
  "&.closed": {
    marginLeft: "-380px",
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
}));

export interface Lead {
  potential: "High" | "Med" | "Low";
  Action: ReactNode;
  email: string;
  id: string;
  name: string;
  jobTitle: string;
  companyName: string;
  industry: string;
  location: string;
}

interface CampaignLead {
  username: string;
  name: string;
  avatar?: string;
  position: string;
  current_company?: {
    name: string;
    title?: string;
    logo_url?: string;
  };
  current_company_name?: string;
  current_company_position?: string;
  city?: string;
  location?: string;
  country_code: string;
  score?: number;
  connections?: number;
  email?: string;
  industry?: string;
  profile_url: string;
  lead_info: {
    lead_id: string;
    lead_name: string;
  };
  lead_source: string;
  lead_status: string;
  black_list: boolean;
  priority_list: boolean;
  final_score: string | null;
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#5175e0",
    },
    background: {
      default: "#f5f5f5",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          minWidth: "auto",
          padding: "12px 16px",
          "&.Mui-selected": {
            color: "#5175e0",
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: "#5175e0",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "12px 16px",
          borderBottom: "1px solid #f0f0f0",
        },
        head: {
          backgroundColor: "#f8f9fa",
          fontWeight: 600,
          color: "#5f6368",
        },
      },
    },
  },
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

const CampaignDetailsNew = () => {
  const [tabValue, setTabValue] = useState<number>(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(0);
  const expertId = useSelector((state: RootState) => state.auth.user.expertId);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const jId = params.get("jobId") || "";
  const [jobId] = useState(jId);

  const statusIconMap: Record<string, JSX.Element> = {
    "All Leads": <GroupIcon sx={{ color: "#325CD6" }} />,
    "In progress": <AccessTimeIcon sx={{ color: "#325CD6" }} />,
    Completed: <MoveToInboxIcon sx={{ color: "#325CD6" }} />,
    Awaiting: <HourglassEmptyIcon sx={{ color: "#325CD6" }} />,
    Paused: <PauseCircleIcon sx={{ color: "#325CD6" }} />,
    Blacklisted: <ReportProblemIcon sx={{ color: "#325CD6" }} />,
  };

  const PotentialTag: React.FC<{ potential: "High" | "Med" | "Low" }> = ({
    potential,
  }) => {
    let backgroundColor, color, icon;

    if (potential === "High") {
      color = "#2e7d32";
      icon = <ArrowUpwardIcon fontSize="small" />;
    } else if (potential === "Med") {
      color = "#DC6803";
      icon = <ArrowUpwardIcon fontSize="small" />;
    } else if (potential === "Low") {
      color = "#c62828";
      icon = <ArrowDownwardIcon fontSize="small" />;
    }

    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "2px 8px",
          borderRadius: "16px",
          backgroundColor,
          color,
          border: `1px solid ${color}`,
          fontSize: "0.75rem",
          fontWeight: 500,
        }}
      >
        {potential} Potential {icon}
      </div>
    );
  };

  const DropdownMultiSelect = ({
    label,
    options,
    searchable = false,
    value,
    onChange,
  }: {
    label: string;
    options: string[];
    searchable?: boolean;
    value: string[];
    onChange: (val: string[]) => void;
  }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const firstMatchRef = useRef<HTMLLIElement | null>(null);

    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
      setSearchTerm("");
    };

    const handleToggleOption = (option: string) => {
      const updated = value.includes(option)
        ? value.filter((item) => item !== option)
        : [...value, option];
      onChange(updated);
    };

    const filteredOptions = options.filter((option) =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
      if (firstMatchRef.current) {
        firstMatchRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }, [searchTerm]);

    return (
      <>
        <Button
          variant="outlined"
          endIcon={<KeyboardArrowDownIcon />}
          size="small"
          onClick={handleClick}
          sx={{
            textTransform: "none",
            color: "#5f6368",
            borderColor: "#D1D5DB",
            background: "#FAFAFA",
            borderRadius: "8px",
            width: 200,
            justifyContent: "space-between",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: "12px",
          }}
        >
          <Box
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "inline-block",
              flexGrow: 1,
            }}
          >
            <Typography
              noWrap
              sx={{
                fontSize: "12px",
                color: "#5f6368",
                textAlign: "left",
              }}
            >
              {selectedOptions.length > 0 ? selectedOptions.join(", ") : label}
            </Typography>
          </Box>
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            style: {
              width: 200,
              maxHeight: 200,
              overflowY: "auto",
              marginTop: "10px",
              border: "1px solid #D1D5DB",
            },
            sx: {
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#ffffff",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#DFE2E7",
                borderRadius: "3px",
              },
            },
          }}
          disableScrollLock
        >
          {searchable && (
            <Box px={1} py={1}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search with keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: "#f5f5f5",
                    borderRadius: "6px",
                    fontSize: "12px",
                  },
                }}
              />
            </Box>
          )}

          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <MenuItem
                key={option}
                ref={index === 0 ? firstMatchRef : null}
                onClick={() => handleToggleOption(option)}
                selected={selectedOptions.includes(option)}
                sx={{
                  fontSize: "12px",
                  py: 0.5,
                  backgroundColor: selectedOptions.includes(option)
                    ? "#e3f2fd"
                    : "#fff",
                  color: selectedOptions.includes(option)
                    ? "#1976d2"
                    : "inherit",
                  "&:hover": {
                    backgroundColor: "#e3f2fd",
                  },
                }}
              >
                <Checkbox
                  checked={selectedOptions.includes(option)}
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": {
                      color: "#1976d2",
                    },
                  }}
                />
                <ListItemText
                  primaryTypographyProps={{ fontSize: "12px" }}
                  primary={option}
                />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled sx={{ fontSize: "12px" }}>
              No results found
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const [openPrioritizeModal, setOpenPrioritizeModal] = useState(false);
  const [openFeedbackModal, setOpenFeedbackModal] = useState(false);
  const [selectedJobTitles, setSelectedJobTitles] = useState<string[]>([]);
  const [selectedLeadStatus, setSelectedLeadStatus] = useState<string[]>([]);
  const [selectedLeadPotential, setSelectedLeadPotential] = useState<string[]>(
    []
  );
  const [openShortlistConfigModal, setOpenShortlistConfigModal] =
    useState(false);
  const [SliderValue, setSliderValue] = useState<number>(45);
  const [campaignId, setCampaignId] = useState<string>("");
  const [campaignName, setCampaignName] = useState<string>("");
  const [campaignStatus, setCampaignStatus] = useState<boolean>(false);
  const [autoSource, setAutoSource] = useState<boolean>(false);
  const [createdAt, setCreatedAt] = useState<number | null>(null);
  const [totalLeads, setTotalLeads] = useState<number | null>();
  const [acceptanceRate, setAcceptanceRate] = useState<number | null>();
  const [replyRate, setReplyRate] = useState<number | null>();
  const [leadStats, setLeadStats] = useState<null | {
    totalLeadsCount: number;
    gettingReadyLeads: number;
    inProgressLeadsCount: number;
    finishedLeadsCount: number;
    failedLeadsCount: number;
    blackListedCount: number;
    pausedCount: number;
    waitingUntilCount: number;
  }>(null);
  const [allLead, setAllLeads] = useState<CampaignLead[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [firstLoad, setFirstLoad] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [selectedUserName, setSelectedUserName] = useState("");
  const [selectedUsernames, setSelectedUsernames] = useState<string[]>([]);
  const [batchIDs, setBatchIDs] = useState<string[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [dripifyIDs, setDripifyIDs] = useState<number[]>([]);
  const [batchID, setBatchID] = useState<string>("");
  const [openLeadDrawer, setOpenLeadDrawer] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [selectedUsernamesForPrioritize, setSelectedUsernamesForPrioritize] =
    useState<string[]>([]);
  const [selectedBlacklistedUsernames, setSelectedBlacklistedUsernames] =
    useState<string[]>([]);
  const [totalProfiles, setTotalProfiles] = useState<number>(0);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [campaignAnalytics, setCampaignAnalytics] = useState<{
    total_leads: number;
    acceptance_rate: number;
    reply_rate: number;
    active: boolean;
    lead_statistics: {
      gettingReadyLeads: number;
      inProgressLeadsCount: number;
      finishedLeadsCount: number;
      failedLeadsCount: number;
      blackListedCount: number;
      pausedCount: number;
      waitingUntilCount: number;
    };
    batchAnalytics: {
      batchId: string;
      acceptRate: number;
      replyRate: number;
      leadCount: number;
    }[];
  } | null>(null);
  const [openAddLeadDialog, setOpenAddLeadDialog] = useState(false);

  const fetchAllCampaignLeads = useCallback(async () => {
    try {
      if (firstLoad) {
        setLoading(true);
      } else {
        setTableLoading(true);
      }

      let payload: {
        jobId: string;
        expertId: string;
        token: string;
        campaignId: string;
        skipCount: number;
        count: number;
        leadIds?: string[];
        searchText?: string;
      } = {
        jobId: jobId,
        expertId: expertId,
        token: DEFAULT_TOKEN,
        campaignId: campaignId,
        skipCount: debouncedSearchText ? 0 : (currentPage - 1) * leadsPerPage,
        count: leadsPerPage,
      };

      if (debouncedSearchText) {
        payload.searchText = debouncedSearchText;
      }

      if (tabValue === 1) {
        if (batchID) {
          payload.leadIds = [batchID];
        } else {
          payload.leadIds = batchIDs;
        }
      }

      const response = await campaignManagementService.getCampaignAllLeads(
        payload
      );
      const campaignAllLeads = response?.output?.leadDetails || [];

      setAllLeads(campaignAllLeads);
      setTotalProfiles(response?.output?.totalProfiles || 0);
    } catch (error) {
      console.log("Error fetching campaign leads", error);
    } finally {
      if (firstLoad) {
        setLoading(false);
        setFirstLoad(false);
      } else {
        setTableLoading(false);
      }
    }
  }, [
    jobId,
    expertId,
    campaignId,
    tabValue,
    currentPage,
    batchIDs,
    batchID,
    debouncedSearchText,
    firstLoad,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      if (searchText && currentPage !== 1) {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText, currentPage]);

  useEffect(() => {
    if (expertId && jobId && campaignId) {
      fetchAllCampaignLeads();
    }
  }, [
    expertId,
    jobId,
    campaignId,
    tabValue,
    currentPage,
    batchID,
    debouncedSearchText,
  ]);

  const setBatchIDChild = (batchId: string) => {
    setBatchID(batchId);
  };

  const leadsPerPage = 10;
  const totalPages = Math.ceil(totalProfiles / leadsPerPage);

  useEffect(() => {
    const fetchCampaignDetails = async () => {
      try {
        const payload = {
          jobId: jobId,
          expertId: expertId,
          token: DEFAULT_TOKEN,
        };

        const response: any =
          await campaignManagementService.getCampaingDetails(payload);
        const campaign = response?.output?.campaigns?.[0];

        if (campaign) {
          setCampaignId(campaign.campaignId);
          setCampaignName(campaign.campaignName);
          const isActive = campaign.campaignStatus === "ACTIVE";
          setCampaignStatus(isActive);
          setCreatedAt(campaign.createdAt);
          const isAutoSourcingActive = campaign.autoSourcing === "ACTIVE";
          setAutoSource(isAutoSourcingActive);
        }
      } catch (error) {
        console.error("Failed to fetch campaign details:", error);
      }
    };

    if (jobId && expertId) {
      fetchCampaignDetails();
    }
  }, [expertId, jobId]);

  const fetchCampaignAnalyticsData = async () => {
    try {
      let payload: any = {
        jobId: jobId,
        expertId: expertId,
        token: DEFAULT_TOKEN,
        campaignId: campaignId,
        leadIds: dripifyIDs,
      };

      const analyticsResponse: any =
        await campaignManagementService.getCampaignAnalytics(payload);
      const leads = analyticsResponse?.output?.campaignLeads ?? [];
      const analytics = analyticsResponse?.output?.campaignAnalytics;

      if (analytics) {
        setCampaignAnalytics({
          total_leads: analytics.total_leads,
          acceptance_rate: analytics.acceptance_rate,
          reply_rate: analytics.reply_rate,
          active: campaignStatus,
          lead_statistics: analytics.lead_statistics,
          batchAnalytics: analytics.batchAnalytics,
        });
        setTotalLeads(analytics.total_leads ?? 0);
        setAcceptanceRate(analytics.acceptance_rate ?? 0);
        setReplyRate(analytics.reply_rate ?? 0);
      }

      const batchIds = leads?.map((cl: any) => cl.batchId) || [];
      const dripifyIds = leads?.map((cl: any) => cl.id) || [];

      let formattedBatches: Batch[] = leads.map((lead: any) => ({
        id: lead.id,
        name: lead.name,
        createdAt: lead.createdAt,
        leadCount: lead.totalLeadsCount,
        acceptRate: 0,
        replyRate: 0,
        batchId: lead.batchId,
      }));

      setBatchIDs(batchIds);
      setDripifyIDs(dripifyIds);

      payload.leadIds = dripifyIds;

      const campaignLeadsAnalyticsResponse: any =
        await campaignManagementService.getCampaignLeadsAnalytics(payload);
      const leadStats =
        campaignLeadsAnalyticsResponse?.output?.lead_statistics || [];

      if (leadStats.length > 0) {
        const aggregatedStats = leadStats.reduce(
          (acc: any, curr: any) => {
            return {
              totalLeadsCount:
                (acc.totalLeadsCount || 0) + (curr.totalLeadsCount || 0),
              gettingReadyLeads:
                (acc.gettingReadyLeads || 0) + (curr.gettingReadyLeads || 0),
              inProgressLeadsCount:
                (acc.inProgressLeadsCount || 0) +
                (curr.inProgressLeadsCount || 0),
              finishedLeadsCount:
                (acc.finishedLeadsCount || 0) + (curr.finishedLeadsCount || 0),
              failedLeadsCount:
                (acc.failedLeadsCount || 0) + (curr.failedLeadsCount || 0),
              blackListedCount:
                (acc.blackListedCount || 0) + (curr.blackListedCount || 0),
              pausedCount: (acc.pausedCount || 0) + (curr.pausedCount || 0),
              waitingUntilCount:
                (acc.waitingUntilCount || 0) + (curr.waitingUntilCount || 0),
            };
          },
          {
            totalLeadsCount: 0,
            gettingReadyLeads: 0,
            inProgressLeadsCount: 0,
            finishedLeadsCount: 0,
            failedLeadsCount: 0,
            blackListedCount: 0,
            pausedCount: 0,
            waitingUntilCount: 0,
          }
        );

        setLeadStats(aggregatedStats);
      } else {
        setLeadStats(null);
      }

      formattedBatches = formattedBatches.map((batch) => {
        const matchingStats = leadStats.find(
          (stat: any) => stat.id === batch.id
        );
        if (matchingStats) {
          return {
            ...batch,
            acceptRate: matchingStats.acceptanceRate || 0,
            replyRate: matchingStats.replyRate || 0,
          };
        }
        return batch;
      });

      setBatches(formattedBatches);
    } catch (error) {
      console.error("Failed to fetch campaign analytics:", error);
      toast.error("Failed to fetch campaign analytics");
    }
  };

  useEffect(() => {
    if (expertId && jobId && campaignId) {
      fetchCampaignAnalyticsData();
    }
  }, [expertId, jobId, campaignId]);

  const handleBlacklistCandidates = useCallback(async () => {
    try {
      const blacklistResponse =
        await campaignManagementService.updateBlackListCandidates({
          token: DEFAULT_TOKEN,
          expertId: expertId,
          jobId: jobId,
          campaignId: campaignId,
          userNames: selectedUsernames.join(", "),
          whiteList: false,
        });

      // await campaignManagementService.PrioritizeLeads({
      //   token: DEFAULT_TOKEN,
      //   expertId: expertId,
      //   jobId: jobId,
      //   campaignId: campaignId,
      //   userNames: selectedUsernames.join(", "),
      //   actionType: "ADD"
      // });

      toast.success("Selected candidates have been blacklisted successfully.");
      setSelectedUsernames([]);
      fetchAllCampaignLeads();
    } catch (error) {
      console.error("Error blacklisting candidates:", error);
      toast.error("Failed to blacklist candidates.");
    }
  }, [expertId, jobId, campaignId, selectedUsernames, fetchAllCampaignLeads]);

  const handlePrioritizeLeads = useCallback(async () => {
    try {
      await campaignManagementService.PrioritizeLeads({
        token: DEFAULT_TOKEN,
        expertId: expertId,
        jobId: jobId,
        campaignId: campaignId,
        userNames: selectedUsernamesForPrioritize.join(", "),
        actionType: "ADD",
      });
      toast.success("Selected leads have been prioritized successfully.");
      setSelectedUsernamesForPrioritize([]);
      setOpenPrioritizeModal(false);
    } catch (error) {
      console.error("Error prioritizing leads:", error);
      toast.error("Failed to prioritize leads.");
    }
  }, [expertId, jobId, campaignId, selectedUsernamesForPrioritize]);

  const handleWhitelistCandidates = async () => {
    try {
      await campaignManagementService.updateBlackListCandidates({
        token: DEFAULT_TOKEN,
        expertId,
        jobId,
        campaignId,
        userNames: selectedBlacklistedUsernames.join(", "),
        whiteList: true,
      });

      toast.success("Selected candidates have been whitelisted successfully.");
      setSelectedBlacklistedUsernames([]);
      setSelectedUsernames([]);
      fetchAllCampaignLeads();
    } catch (error) {
      console.error("Error whitelisting candidates:", error);
      toast.error("Failed to whitelist candidates.");
    }
  };

  const handleFeedbackSubmit = async () => {
    try {
      await campaignManagementService.addCampaignLeadFeedback({
        token: DEFAULT_TOKEN,
        expertId: expertId,
        jobId: jobId,
        campaignId: parseInt(campaignId),
        userName: selectedUserName,
        feedback: feedbackText,
      });
      setOpenFeedbackModal(false);
      setFeedbackText("");
      toast.success("Feedback submitted successfully.");
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback.");
    }
  };

  const handleShortlistConfigSubmit = async () => {
    setSelectedUsernames([]);

    try {
      await campaignManagementService.updateScoreThreshold({
        token: DEFAULT_TOKEN,
        expertId,
        jobId,
        campaignId,
        threshold: SliderValue,
      });

      toast.success("Shortlisting benchmark updated successfully.");
      setOpenShortlistConfigModal(false);
    } catch (error) {
      console.error("Error updating benchmark", error);
      setOpenShortlistConfigModal(false);
    }
  };

  const handleCandidateDrawerClose = useCallback(() => {
    fetchAllCampaignLeads();
  }, [fetchAllCampaignLeads]);

  const leadStatusMap = [
    { label: "All Leads", key: "totalLeadsCount" },
    { label: "In progress", key: "inProgressLeadsCount" },
    { label: "Completed", key: "finishedLeadsCount" },
    { label: "Awaiting", key: "waitingUntilCount" },
    { label: "Paused", key: "pausedCount" },
    { label: "Blacklisted", key: "blackListedCount" },
  ];

  const filteredLeads = useMemo(
    () =>
      allLead.map((lead) => ({
        ...lead,
        score: lead.final_score ? parseFloat(lead.final_score) : undefined,
        current_company: lead.current_company_name
          ? {
              name: lead.current_company_name,
              title: lead.current_company_position || undefined,
            }
          : undefined,
      })),
    [allLead]
  );

  const handleShortlistCandidates = useCallback(async () => {
    try {
      const selectedLeads = filteredLeads.filter((lead) =>
        selectedUsernames.includes(lead.username)
      );
      const profileUrls = selectedLeads
        .map((lead) => lead.profile_url)
        .filter((url) => url);

      if (profileUrls.length === 0) {
        toast.error("No valid profile URLs found in selected leads");
        return;
      }

      const payload = {
        token: DEFAULT_TOKEN,
        expertId: expertId,
        jobId: jobId,
        campaignId: campaignId,
        leadType: "PROFILES",
        profiles: profileUrls,
        documentUrl: "",
      };

      const response = await campaignManagementService.AddLead(payload);

      if (response) {
        toast.success("Candidates shortlisted successfully");
        setSelectedUsernames([]);
        fetchAllCampaignLeads();
      }
    } catch (error) {
      console.error("Error shortlisting candidates:", error);
      toast.error("Failed to shortlist candidates");
    }
  }, [
    expertId,
    jobId,
    campaignId,
    selectedUsernames,
    filteredLeads,
    fetchAllCampaignLeads,
  ]);

  const TabActionButton = useCallback(() => {
    const BlackListed = selectedUsernames.every(
      (username) =>
        filteredLeads.find((lead) => lead.username === username)?.black_list
    );

    switch (tabValue) {
      case 0:
        if (tabValue !== 0 || selectedUsernames.length === 0) return null;

        return (
          <Box display="flex" gap={2}>
            <Button
              startIcon={<KeyboardDoubleArrowUpRoundedIcon fontSize="small" />}
              onClick={() => setOpenPrioritizeModal(true)}
              disabled={BlackListed}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "6px",
                padding: "8px 16px",
                border: "1px solid #D0D5DD",
                backgroundColor: "#FAFAFA",
                color: "#101828",
                fontWeight: 600,
                fontSize: "14px",
                textTransform: "none",
                boxShadow: "none",
                lineHeight: 1.5,
              }}
            >
              Prioritize lead's evaluation
            </Button>

            <Button
              startIcon={<FilterAltIcon fontSize="small" />}
              onClick={handleShortlistCandidates}
              disabled={BlackListed}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "6px",
                padding: "8px 16px",
                border: "1px solid #D0D5DD",
                backgroundColor: "#FAFAFA",
                color: "#101828",
                fontWeight: 600,
                fontSize: "14px",
                textTransform: "none",
                boxShadow: "none",
                lineHeight: 1.5,
              }}
            >
              Shortlist Candidates
            </Button>

            <Button
              startIcon={<ReportProblemIcon fontSize="small" />}
              onClick={handleBlacklistCandidates}
              disabled={BlackListed}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "6px",
                padding: "8px 16px",
                border: "1px solid #D0D5DD",
                backgroundColor: "#FAFAFA",
                color: "#101828",
                fontWeight: 600,
                fontSize: "14px",
                textTransform: "none",
                boxShadow: "none",
                lineHeight: 1.5,
              }}
            >
              Blacklist Candidates
            </Button>

            {selectedBlacklistedUsernames.length > 0 && (
              <Button
                startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                onClick={handleWhitelistCandidates}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  border: "1px solid #D0D5DD",
                  backgroundColor: "#FAFAFA",
                  color: "#101828",
                  fontWeight: 600,
                  fontSize: "14px",
                  textTransform: "none",
                  boxShadow: "none",
                  lineHeight: 1.5,
                }}
              >
                Whitelist Candidates
              </Button>
            )}
          </Box>
        );

      case 1:
        return (
          <Button
            variant="outlined"
            startIcon={<KeyboardDoubleArrowUpRoundedIcon />}
            sx={commonButtonStyle}
            onClick={() => setOpenShortlistConfigModal(true)}
            disabled={selectedUsernames.length > 0}
          >
            Configure shortlisting benchmark
          </Button>
        );

      default:
        return null;
    }
  }, [
    tabValue,
    selectedUsernames,
    filteredLeads,
    selectedBlacklistedUsernames,
    handleBlacklistCandidates,
  ]);

  const CreatedAtDateFormat = createdAt
    ? new Date(createdAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "Fetching...";

  const handleCampaignStatusChange = async ({
    newStatus,
    autoSource,
  }: {
    newStatus?: boolean;
    autoSource?: boolean;
  }) => {
    try {
      const status =
        newStatus !== undefined
          ? newStatus
            ? "ACTIVE"
            : "NOT_ACTIVE"
          : undefined;

      const aS =
        autoSource !== undefined
          ? autoSource
            ? "ACTIVE"
            : "NOT_ACTIVE"
          : undefined;

      const response = await campaignManagementService.updateCampaignStatus(
        expertId,
        campaignId,
        status,
        aS
      );

      if (response?.apiStatus === "SUCCESS") {
        if (newStatus !== undefined) {
          setCampaignStatus(newStatus);
          toast.success("Campaign status updated successfully");
        }
        if (autoSource !== undefined) {
          setAutoSource(autoSource);
          toast.success("Campaign auto source updated successfully");
        }
        return;
      }

      toast.error("Failed to update campaign details");
      const detailsPayload = {
        jobId,
        expertId,
        token: DEFAULT_TOKEN,
      };

      const detailsResponse =
        await campaignManagementService.getCampaingDetails(detailsPayload);

      const campaign = detailsResponse?.output?.campaigns?.[0];

      if (campaign) {
        if (newStatus !== undefined) {
          const serverStatus = campaign.campaignStatus === "ACTIVE";
          setCampaignStatus(serverStatus);
        }
        if (autoSource !== undefined) {
          setAutoSource(!!campaign.autoSourcing);
        }
      }
    } catch (error) {
      console.error("Failed to update campaign status:", error);
      toast.error("Failed to update campaign status");
    }
  };

  if (loading) {
    return <CampaignDetailsSkeleton />;
  }
  return (
    <OuterDiv style={{ background: "#F8FAFF" }}>
      <ThemeProvider theme={theme}>
        <Box className="d-flex justify-content-between align-items-center bg-white px-4 py-3 border-bottom w-100 p-sticky w-">
          <Box>
            <Typography fontSize="16px">{campaignName}</Typography>
          </Box>
          <Box>
            <Button
              onClick={() => setOpenAddLeadDialog(true)}
              className="bg-primary rounded px-3 py-2 text-white fw-semibold fs-7 text-decoration-none me-2"
            >
              Add Leads
            </Button>
          </Box>
        </Box>
        <Box className="d-flex h-100 w-100 p-3">
          {sidebarOpen && (
            <SidebarWrapper className={sidebarOpen ? "" : "closed"}>
              <ShortlistedLeadsContainer
                setBatchIDChild={setBatchIDChild}
                batches={batches}
              />
            </SidebarWrapper>
          )}
          <Box className="flex-grow-1 overflow-auto">
            <Box
              sx={{
                display: "flex",
                position: "relative",
                justifyContent: "space-between",
              }}
            >
              <CampaignHeader
                campaignId={campaignId}
                campaignName={campaignName}
                totalLeads={totalLeads ?? 0}
                acceptanceRate={acceptanceRate ?? 0}
                replyRate={replyRate ?? 0}
                createdAt={CreatedAtDateFormat}
                campaignStatus={campaignStatus}
                expertId={expertId}
                showSidebarToggle={tabValue === 1}
                sidebarOpen={sidebarOpen}
                onSidebarToggle={() => {
                  setSidebarOpen(!sidebarOpen);
                  setBatchIDChild("");
                }}
                onStatusChange={handleCampaignStatusChange}
                autoSource={autoSource}
              />
            </Box>

            <Box>
              <Box
                className="p-3"
                sx={{
                  bgcolor: "#fff",
                  border: "1px solid #DCDFE5",
                  borderRadius: "8px",
                  marginTop: "20px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <Tabs
                    value={tabValue}
                    onChange={(e, newValue) => {
                      setTabValue(newValue);
                      if (newValue === 0) {
                        setSidebarOpen(false);
                      }
                    }}
                  >
                    {[
                      "All Leads",
                      "Shortlisted Leads",
                      // "Sequence",
                      // "Statistics",
                      // "Settings",
                    ].map((label, index) => (
                      <Tab
                        key={label}
                        label={label}
                        value={index}
                        sx={tabStyle(tabValue === index)}
                      />
                    ))}
                  </Tabs>

                  {TabActionButton()}
                </Box>

                {tabValue === 0 && (
                  <>
                    <Box>
                      <StatusCards leadStats={leadStats} />

                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          justifyContent: "space-between",
                        }}
                      >
                        <Box>
                          <TextField
                            placeholder="Search with keywords..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                              ),
                            }}
                            variant="outlined"
                            size="small"
                            sx={{ width: 250 }}
                          />
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          mb: 2,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        {selectedJobTitles.map((title) => (
                          <Chip
                            key={`job-${title}`}
                            label={`Job Title: ${title}`}
                            onDelete={() =>
                              setSelectedJobTitles((prev) =>
                                prev.filter((item) => item !== title)
                              )
                            }
                            deleteIcon={<CloseIcon />}
                            sx={{
                              bgcolor: "#e8f0fe",
                              color: "#3366cc",
                              border: "1px solid #3366cc",
                              "& .MuiChip-deleteIcon": { color: "#3366cc" },
                              fontWeight: 500,
                            }}
                          />
                        ))}

                        {selectedLeadStatus.map((status) => (
                          <Chip
                            key={`status-${status}`}
                            label={`Status: ${status}`}
                            onDelete={() =>
                              setSelectedLeadStatus((prev) =>
                                prev.filter((item) => item !== status)
                              )
                            }
                            deleteIcon={<CloseIcon />}
                            sx={{
                              bgcolor: "#e8f0fe",
                              color: "#3366cc",
                              border: "1px solid #3366cc",
                              "& .MuiChip-deleteIcon": { color: "#3366cc" },
                              fontWeight: 500,
                            }}
                          />
                        ))}

                        {selectedLeadPotential.map((potential) => (
                          <Chip
                            key={`potential-${potential}`}
                            label={`Potential: ${potential}`}
                            onDelete={() =>
                              setSelectedLeadPotential((prev) =>
                                prev.filter((item) => item !== potential)
                              )
                            }
                            deleteIcon={<CloseIcon />}
                            sx={{
                              bgcolor: "#e8f0fe",
                              color: "#3366cc",
                              border: "1px solid #3366cc",
                              "& .MuiChip-deleteIcon": { color: "#3366cc" },
                              fontWeight: 500,
                            }}
                          />
                        ))}
                      </Box>
                      {tableLoading ? (
                        <CampaignTableSkeleton />
                      ) : (
                        <LeadsTable
                          leads={filteredLeads}
                          columns={[
                            {
                              id: "name",
                              label: "Name",
                              render: (lead: CampaignLead) => (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    color: "#5175e0",
                                    fontWeight: "bold",
                                  }}
                                  onClick={() => {
                                    setSelectedCandidate({
                                      username: lead.username,
                                      jobId,
                                      expertId,
                                      campaignId,
                                      lead_info: lead.lead_info,
                                      black_list: lead.black_list,
                                      priority_list: lead.priority_list,
                                    });
                                    setOpenLeadDrawer(true);
                                  }}
                                >
                                  {lead.black_list && (
                                    <Tooltip title="Blacklisted User" arrow>
                                      <NoAccountsIcon
                                        fontSize="small"
                                        sx={{
                                          color: "#000",
                                          width: 16,
                                          height: 16,
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                  {lead.priority_list && (
                                    <Tooltip title="Prioritize User" arrow>
                                      <KeyboardDoubleArrowUpRoundedIcon
                                        fontSize="small"
                                        sx={{
                                          color: "#000",
                                          width: 16,
                                          height: 16,
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                  <span>{lead.name}</span>
                                </div>
                              ),
                            },

                            {
                              id: "potential",
                              label: "Lead Potential",
                              render: (lead: CampaignLead) => (
                                <PotentialTag
                                  potential={
                                    parseFloat(lead.final_score || "0") > 80
                                      ? "High"
                                      : parseFloat(lead.final_score || "0") >=
                                        50
                                      ? "Med"
                                      : "Low"
                                  }
                                />
                              ),
                            },
                            {
                              id: "jobTitle",
                              label: "Job Title",
                              render: (lead: CampaignLead) => (
                                <Tooltip title={lead.position || ""} arrow>
                                  <span>
                                    {lead.position?.length > 30
                                      ? `${lead.position.substring(0, 30)}...`
                                      : lead.position}
                                  </span>
                                </Tooltip>
                              ),
                            },
                            {
                              id: "email",
                              label: "Email",
                              render: (Lead: CampaignLead) =>
                                Lead.email || "N/A",
                            },
                            {
                              id: "companyName",
                              label: "Company Name",
                              render: (lead: CampaignLead) =>
                                lead.current_company?.name ||
                                lead.current_company_name,
                            },
                            {
                              id: "location",
                              label: "Location",
                              render: (lead: CampaignLead) =>
                                `${lead.location || lead.city}, ${
                                  lead.country_code
                                }`,
                            },
                            {
                              id: "industry",
                              label: "Industry",
                              render: (lead: CampaignLead) =>
                                lead.industry || "N/A",
                            },
                            {
                              id: "action",
                              label: "Action",
                              render: (lead: CampaignLead) => (
                                <IconButton
                                  onClick={() => {
                                    setOpenFeedbackModal(true);
                                    setSelectedUserName(lead.username);
                                  }}
                                >
                                  <ChatBubbleOutlineRoundedIcon />
                                </IconButton>
                              ),
                            },
                          ]}
                          totalProfiles={totalProfiles}
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                          selectedUsernames={selectedUsernames}
                          onSelectLead={(
                            username: string,
                            checked: boolean
                          ) => {
                            const lead = filteredLeads.find(
                              (l) => l.username === username
                            );
                            setSelectedUsernames((prev) =>
                              checked
                                ? [...prev, username]
                                : prev.filter((name) => name !== username)
                            );
                            setSelectedUsernamesForPrioritize((prev) =>
                              checked
                                ? [...prev, username]
                                : prev.filter((name) => name !== username)
                            );
                            if (lead?.black_list) {
                              setSelectedBlacklistedUsernames((prev) =>
                                checked
                                  ? [...prev, username]
                                  : prev.filter((name) => name !== username)
                              );
                            }
                          }}
                          onSelectAll={(checked: boolean) => {
                            const usernames = filteredLeads.map(
                              (lead) => lead.username
                            );
                            setSelectedUsernames(checked ? usernames : []);
                            setSelectedUsernamesForPrioritize(
                              checked ? usernames : []
                            );
                            const blacklistedUsernames = filteredLeads
                              .filter((lead) => lead.black_list)
                              .map((lead) => lead.username);
                            setSelectedBlacklistedUsernames(
                              checked ? blacklistedUsernames : []
                            );
                          }}
                        />
                      )}
                    </Box>
                  </>
                )}

                <Box sx={{ marginTop: "20px" }}>
                  {tabValue === 1 && (
                    <>
                      {tableLoading ? (
                        <CampaignTableSkeleton />
                      ) : (
                        <ShortlistedLeadsTable
                          leads={filteredLeads}
                          totalProfiles={totalProfiles}
                          selectedJobTitles={selectedJobTitles}
                          selectedLeadStatus={selectedLeadStatus}
                          selectedLeadPotential={selectedLeadPotential}
                          setSelectedJobTitles={setSelectedJobTitles}
                          setSelectedLeadStatus={setSelectedLeadStatus}
                          setSelectedLeadPotential={setSelectedLeadPotential}
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                          onLeadClick={(lead: any) => {
                            setSelectedCandidate({
                              username: lead.username,
                              jobId,
                              expertId,
                              campaignId,
                              lead_info: lead.lead_info,
                              black_list: lead.black_list,
                              prioritize_list: lead.prioritize_list,
                            });
                            setOpenLeadDrawer(true);
                          }}
                          selectedUsernames={selectedUsernames}
                          setSelectedUsernames={setSelectedUsernames}
                        />
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </ThemeProvider>
      <PrioritizeDialog
        open={openPrioritizeModal}
        onClose={() => setOpenPrioritizeModal(false)}
        onConfirm={handlePrioritizeLeads}
      />

      <FeedbackDialog
        open={openFeedbackModal}
        onClose={() => setOpenFeedbackModal(false)}
        onSubmit={handleFeedbackSubmit}
        feedbackText={feedbackText}
        onFeedbackChange={(text) => setFeedbackText(text)}
      />

      <ShortlistConfigDialog
        open={openShortlistConfigModal}
        onClose={() => setOpenShortlistConfigModal(false)}
        onSubmit={handleShortlistConfigSubmit}
        sliderValue={SliderValue}
        onSliderChange={(value) => setSliderValue(value)}
      />

      {selectedCandidate && (
        <LeadDetailsDrawer
          open={openLeadDrawer}
          onClose={() => setOpenLeadDrawer(false)}
          candidateDetails={selectedCandidate}
          setSelectedUserName={(username) => setSelectedUserName(username)}
          openFeedBackModal={() => setOpenFeedbackModal(true)}
          onCandidateDrawerClose={handleCandidateDrawerClose}
        />
      )}
      <AddLeadDialog
        open={openAddLeadDialog}
        onClose={() => setOpenAddLeadDialog(false)}
        expertId={expertId}
        jobId={jobId}
        campaignId={campaignId}
        onActionPerformed={fetchAllCampaignLeads}
      />
    </OuterDiv>
  );
};

export default React.memo(CampaignDetailsNew);
